import React, { useState, useEffect, useCallback, memo } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Grid, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { ClassNameMap, withStyles } from '@mui/styles'
import { useSelector, useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import stylesheet from '../detail-container.stylesheet'
import { RootState } from '../../../../redux/store'
import { currentStatus } from '../../../../redux/add-control-slice'
import CardLayoutComponent from '../common/card-layout.component'
import DeleteCard from '../common/delete-card-component'
import EmptyCardComponent from '../common/empty-card.component'
import ExperimentDialogComponent from '../common/experiment-dialog.component'
import Helper from '../../../../utils/helper'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'
import { experimentService, ExperimentTemplate, Experiment } from '../../../../services/experiment.service'

interface IProps { input?: any; classes: ClassNameMap }
type ExpRow = Experiment & { mode?: string }

const CHANGE_LABEL: Record<string, string> = { observe: 'Observation', add: 'Started', modify_dose: 'Dose change', remove: 'Stopped' }
const STATUS_LABEL: Record<string, string> = { proposed: 'Proposed', active: 'Active', closed: 'Closed', canceled: 'Canceled' }

// Flatten patientDetail.medications ({ care_plans, other_medications, ... }) into
// perturbation candidates. A med object carries a numeric id, a name, and
// med-specific fields; containers do not.
const MED_FIELDS = ['source_type', 'dosage_info', 'c_schedule', 'sig_directions', 'med_name', 'rxnorm', 'pu_order']
const flattenMeds = (node: any, acc: { id: number; name: string }[] = [], seen = new Set<number>()): { id: number; name: string }[] => {
  if (!node || typeof node !== 'object') return acc
  if (Array.isArray(node)) { node.forEach((n) => flattenMeds(n, acc, seen)); return acc }
  if (typeof node.id === 'number' && typeof node.name === 'string' && node.name && MED_FIELDS.some((f) => f in node) && !seen.has(node.id)) {
    seen.add(node.id); acc.push({ id: node.id, name: node.name })
  }
  Object.values(node).forEach((v) => flattenMeds(v, acc, seen))
  return acc
}

/**
 * The Experiments Treatments section — same machinery as Monitors/Lifestyle:
 * each experiment is a CardLayoutComponent card; "Add new" (section header) and
 * a card's Edit both open ExperimentDialogComponent (a modal); delete is a
 * DeleteCard.
 */
const ProgramExperimentsComponent: React.FC<IProps> = ({ classes }) => {
  const dispatch = useDispatch() as any
  const patientDetail = useSelector((s: RootState) => s.patientDetail.patientDetail)
  const addControl = useSelector((s: RootState) => (s as any).addButtons?.addControl) as any[]
  const refId = patientDetail?.ref_id
  const patientId = patientDetail?.id
  const addFlag = !!addControl?.find((e: any) => e.key === 'MD-EXP')?.value
  const medications = flattenMeds(patientDetail?.medications)
  const { updatePatientData } = useUpdateDataAction(patientId)

  const [templates, setTemplates] = useState<ExperimentTemplate[]>([])
  const [experiments, setExperiments] = useState<ExpRow[]>([])
  const [loading, setLoading] = useState(true)
  const [dialog, setDialog] = useState<{ open: boolean; experiment: Experiment | null }>({ open: false, experiment: null })

  const load = useCallback(async () => {
    if (!refId) return
    setLoading(true)
    try {
      const [tpls, exps] = await Promise.all([experimentService.getTemplates(), experimentService.getExperiments(refId)])
      setTemplates(tpls)
      setExperiments(exps.map((e) => ({ ...e, mode: 'view' })))
    } catch (e) {
      toast.error('Could not load interventions.', Helper.bottom_center())
    } finally { setLoading(false) }
  }, [refId])
  useEffect(() => { void load() }, [load])

  // "Add new" in the section header flips the MD-EXP flag -> open the create modal once.
  useEffect(() => {
    if (addFlag) {
      setDialog({ open: true, experiment: null })
      dispatch(currentStatus({ key: 'MD-EXP', value: false }))
    }
  }, [addFlag, dispatch])

  const closeDialog = () => setDialog({ open: false, experiment: null })
  // Refresh the experiments AND the patient detail: a questionnaire readout bridges to
  // a tracked calc server-side, so the questionnaire (Toxicity) panel must re-fetch to
  // show it without a manual page reload.
  const onSaved = () => { closeDialog(); load(); updatePatientData({ isMeasurement: true }) }
  const onEdit = (id: any) => {
    const exp = experiments.find((e) => e.id === id)
    if (exp) setDialog({ open: true, experiment: exp })
  }
  const onDelIcon = (id: any) => setExperiments((es) => es.map((e) => (e.id === id ? { ...e, mode: 'delete' } : e)))
  const closeDelete = (id: any) => setExperiments((es) => es.map((e) => (e.id === id ? { ...e, mode: 'view' } : e)))
  const confirmDelete = (id: any) =>
    refId && experimentService.deleteExperiment(refId, id).then(() => load())
      .catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))

  const cardSubHeader = (exp: Experiment) =>
    exp.perturbation_label ? `${exp.perturbation_label} · ${CHANGE_LABEL[exp.change] || exp.change}` : 'Observation — no intervention'

  const experimentCard = (exp: ExpRow) => (
    <React.Fragment key={exp.id}>
      {exp.mode === 'delete'
        ? <DeleteCard id={exp.id} header={exp.name} closeDelete={() => closeDelete(exp.id)} outerDelete={() => confirmDelete(exp.id)} />
        : <CardLayoutComponent
            id={exp.id}
            leftHeader={exp.name}
            rightHeader={STATUS_LABEL[exp.status] || exp.status}
            subHeader={cardSubHeader(exp)}
            bottomText={''}
            icons={[{ iconType: 'edit', callback: onEdit }, { iconType: 'delete', callback: onDelIcon }, { iconType: 'more', callback: () => {} }]}
            needs_confirmation={false}
            // An experiment has no needs-update state. This was a hardcoded `true`,
            // which routes CardLayoutComponent to confirmCardBackground and painted
            // every experiment card yellow as though it were awaiting entry
            // (RD 2026-08-08). Nothing read it as a signal; it just coloured the card.
            needs_update={false}
            programs={[]}
            is_referred={false}
            is_overridden={false}
            onRefClick={() => {}}
            patientId={String(patientId ?? '')}
            entityRef={'monitor'}
            category={'medications'}
          />
      }
    </React.Fragment>
  )

  // One accordion per lifecycle group, in the monitors/lifestyle shape. The groups are
  // the D19 distinction made visible: an ACTIVE plan is charting and asking the patient
  // for check-ins, a PROPOSED one is authored and doing neither, and a retired one has
  // been shut off. A proposed plan listed under "Active" mis-states all of that.
  // Retired is collapsed by default — it is history, kept but not the working set.
  const group = (label: string, rows: ExpRow[], expanded: boolean) => (
    rows.length === 0 ? null : (
      <Accordion key={label} square defaultExpanded={expanded} className={classes.patientAccrodionBg}>
        <AccordionSummary expandIcon={<ExpandMoreIcon className={classes.patientAccrodionExpandIcon} />}>
          <Typography className={classes.subTitle}>{label}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container>
            <Grid item className={classes.fullWidth}>{rows.map(experimentCard)}</Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  )

  if (loading) return <CircularProgress size={18} sx={{ m: 1 }} />

  return (
    <Box className={classes.fullWidth}>
      {experiments.length === 0
        ? <EmptyCardComponent />
        : <>
            {group('Active', experiments.filter((e) => e.status === 'active'), true)}
            {group('Proposed', experiments.filter((e) => e.status === 'proposed'), true)}
            {group('Retired', experiments.filter((e) => e.status === 'closed' || e.status === 'canceled'), false)}
          </>
      }
      {dialog.open && refId && (
        <ExperimentDialogComponent
          open={dialog.open}
          handleClose={closeDialog}
          onSaved={onSaved}
          refId={refId}
          templates={templates}
          medications={medications}
          patientId={patientId ?? ''}
          experiment={dialog.experiment}
        />
      )}
    </Box>
  )
}

export default withStyles(stylesheet)(memo(ProgramExperimentsComponent))
